import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const useApplicationStore = create(
  persist(
    (set, get) => ({
      // Application state
      currentStep: 1,
      totalSteps: 3,
      
      // Lead data (from eligibility check)
      leadData: null,
      
      // UTM / campaign tracking data captured from URL query params
      utm: {
        utmSource: null,
        utmMedium: null,
        utmCampaign: null,
      },
      
      // Whether UTM was captured from URL and should persist across navigation
      utmPersisted: false,
      
      // Personal details
      personalDetails: {
        fullName: '',
        panNumber: '',
        gender: '',
        emailId: '',
        aadharNumber: '',
        dateOfBirth: '',
        mobile: '',
        permanentAddress: {
          addressLine: '',
          landmark: '',
          city: '',
          state: '',
          country: 'India',
          pincode: ''
        },
        currentAddress: {
          addressLine: '',
          landmark: '',
          city: '',
          state: '',
          country: 'India',
          pincode: ''
        },
        isSameAsCurrent: true,
        currentResidenceDurationYears: '',
        currentResidenceType: ''
      },
      
      // Professional details
      professionalDetails: {
        employmentType: '',
        companyName: '',
        jobFunction: '',
        designation: '',
        workSector: '',
        organization: '',
        workingSince: '',
        officialEmail: '',
        salaryReceivedType: '',
        netMonthlyIncome: '',
        workAddress: {
          address: '',
          locality: '',
          pincode: '',
          city: '',
          state: ''
        }
      },
      
      // Bank details
      bankDetails: {
        accountNumber: '',
        confirmAccountNumber: '',
        ifsc: '',
        bankName: '',
        branch: '',
        accountType: 'SAVINGS',
        upiId: '',
        nachConsent: false,
        nachConsentDate: ''
      },
      
      // Document upload data
      documentData: {
        profilePicture: null,
        aadharCard: null,
        panCard: null,
        bankStatement: null
      },
      
      // Selected loan plan from PersonalDetailsStep
      selectedLoanPlan: null,
      
      // Form validation errors
      errors: {
        personal: {},
        professional: {},
        bank: {},
        documents: {}
      },
      
      // Actions
      setLeadData: (data) => {
        // Reset all application data when new lead is set
        set({ 
          leadData: data,
          currentStep: 1,  // Reset to first step when new lead is set
          personalDetails: {
            fullName: '',
            panNumber: '',
            gender: '',
            emailId: '',
            aadharNumber: '',
            dateOfBirth: '',
            mobile: '',
            permanentAddress: {
              addressLine: '',
              landmark: '',
              city: '',
              state: '',
              country: 'India',
              pincode: ''
            },
            currentAddress: {
              addressLine: '',
              landmark: '',
              city: '',
              state: '',
              country: 'India',
              pincode: ''
            },
            isSameAsCurrent: true,
            currentResidenceDurationYears: '',
            currentResidenceType: ''
          },
          professionalDetails: {
            employmentType: '',
            companyName: '',
            jobFunction: '',
            designation: '',
            workSector: '',
            organization: '',
            workingSince: '',
            officialEmail: '',
            salaryReceivedType: '',
            netMonthlyIncome: '',
            workAddress: {
              address: '',
              locality: '',
              pincode: '',
              city: '',
              state: ''
            }
          },
          documentData: {
            profilePicture: null,
            aadharCard: null,
            panCard: null,
            bankStatement: null
          },
          errors: {
            personal: {},
            professional: {},
            documents: {}
          }
        });
      },
      
      // Set or merge UTM tracking data (e.g. from URL params)
      setUtmData: (data) => {
        set((state) => ({
          utm: {
            ...state.utm,
            ...data
          },
          utmPersisted: true
        }));
      },
      
      // Reset UTM tracking data to nulls
      resetUtm: () => {
        set(() => ({
          utm: {
            utmSource: null,
            utmMedium: null,
            utmCampaign: null
          },
          utmPersisted: false
        }));
      },
      
      setPersonalDetails: (data) => set({ personalDetails: data }),
      updatePersonalDetail: (field, value) => {
        console.log('updatePersonalDetail called with:', { field, value });
        set((state) => {
          if (field.includes('.')) {
            // Handle nested field updates (e.g., 'currentAddress.pincode')
            const [parentKey, childKey] = field.split('.');
            console.log('Updating nested field:', { parentKey, childKey, value });
            return {
              personalDetails: {
                ...state.personalDetails,
                [parentKey]: {
                  ...state.personalDetails[parentKey],
                  [childKey]: value
                }
              }
            };
          } else {
            // Handle direct field updates
            console.log('Updating direct field:', { field, value });
            return {
              personalDetails: {
                ...state.personalDetails,
                [field]: value
              }
            };
          }
        });
      },
      
      setProfessionalDetails: (data) => set({ professionalDetails: data }),
      updateProfessionalDetail: (field, value) => 
        set((state) => {
          if (field.includes('.')) {
            // Handle nested field updates (e.g., 'workAddress.city')
            const [parentKey, childKey] = field.split('.');
            return {
              professionalDetails: {
                ...state.professionalDetails,
                [parentKey]: {
                  ...state.professionalDetails[parentKey],
                  [childKey]: value
                }
              }
            };
          } else {
            // Handle direct field updates
            return {
              professionalDetails: {
                ...state.professionalDetails,
                [field]: value
              }
            };
          }
        }),
      
      setBankDetails: (data) => set({ bankDetails: data }),
      updateBankDetail: (field, value) => {
        console.log(`updateBankDetail called with field: ${field}, value:`, value);
        set((state) => {
          return {
            bankDetails: {
              ...state.bankDetails,
              [field]: value
            }
          };
        });
      },
      
      setDocumentData: (data) => set({ documentData: data }),
      updateDocumentData: (field, value) => {
        console.log(`updateDocumentData called with field: ${field}, value:`, value);
        set((state) => {
          const newDocumentData = {
            ...state.documentData,
            [field]: value
          };
          console.log('New documentData:', newDocumentData);
          return { documentData: newDocumentData };
        });
      },
      
      setSelectedLoanPlan: (plan) => set({ selectedLoanPlan: plan }),
      
      setErrors: (section, errors) => 
        set((state) => ({
          errors: {
            ...state.errors,
            [section]: errors
          }
        })),
      
      clearErrors: (section) => 
        set((state) => ({
          errors: {
            ...state.errors,
            [section]: {}
          }
        })),
      
      setCurrentStep: (step) => set({ currentStep: step }),
      nextStep: () => set((state) => ({ 
        currentStep: Math.min(state.currentStep + 1, state.totalSteps) 
      })),
      previousStep: () => set((state) => ({ 
        currentStep: Math.max(state.currentStep - 1, 1) 
      })),
      
      // Validation helpers
      isPersonalDetailsValid: () => {
        const { personalDetails, errors } = get();
        
        // Check basic required fields
        const basicFields = ['fullName', 'panNumber', 'gender', 'emailId', 'aadharNumber', 'dateOfBirth', 'mobile'];
        const hasAllBasicFields = basicFields.every(field => personalDetails[field] && personalDetails[field].trim());
        
        // Check permanent address
        const permanentAddress = personalDetails.permanentAddress;
        const hasPermanentAddress = permanentAddress && 
          permanentAddress.addressLine && permanentAddress.addressLine.trim() &&
          permanentAddress.city && permanentAddress.city.trim() &&
          permanentAddress.state && permanentAddress.state.trim() &&
          permanentAddress.pincode && permanentAddress.pincode.trim() &&
          permanentAddress.landmark && permanentAddress.landmark.trim();
        
        // Check current address (only if not same as permanent)
        let hasCurrentAddress = true;
        if (!personalDetails.isSameAsCurrent) {
          const currentAddress = personalDetails.currentAddress;
          hasCurrentAddress = currentAddress && 
            currentAddress.addressLine && currentAddress.addressLine.trim() &&
            currentAddress.city && currentAddress.city.trim() &&
            currentAddress.state && currentAddress.state.trim() &&
            currentAddress.pincode && currentAddress.pincode.trim() &&
            currentAddress.landmark && currentAddress.landmark.trim();
        }
        
        // Check residence details
        const hasResidenceDetails = personalDetails.currentResidenceDurationYears && 
          personalDetails.currentResidenceType;
        
        const hasNoErrors = Object.keys(errors.personal).length === 0;
        
        return hasAllBasicFields && hasPermanentAddress && hasCurrentAddress && hasResidenceDetails && hasNoErrors;
      },
      
      isProfessionalDetailsValid: () => {
        const { professionalDetails, errors } = get();
        const requiredFields = ['employmentType', 'companyName', 'jobFunction', 'designation', 'workSector', 'organization', 'workingSince', 'salaryReceivedType', 'netMonthlyIncome'];
        
        // Check basic required fields
        const hasAllBasicFields = requiredFields.every(field => {
          const value = professionalDetails[field];
          if (field === 'netMonthlyIncome') {
            return value && !isNaN(parseInt(value)) && parseInt(value) > 0;
          }
          return value && (typeof value === 'string' ? value.trim() : value);
        });
        
        // Check workAddress object - require address, pincode, city, state, locality
        const workAddress = professionalDetails.workAddress;
        const hasWorkAddress = workAddress && 
          workAddress.address && workAddress.address.trim() &&
          workAddress.pincode && workAddress.pincode.trim() &&
          workAddress.city && workAddress.city.trim() &&
          workAddress.state && workAddress.state.trim() &&
          workAddress.locality && workAddress.locality.trim();
        
        const hasAllFields = hasAllBasicFields && hasWorkAddress;
        const hasNoErrors = Object.keys(errors.professional).length === 0;
        
        console.log('Store validation check:', {
          hasAllBasicFields,
          hasWorkAddress,
          hasAllFields,
          hasNoErrors,
          workAddress,
          errors: errors.professional
        });
        
        return hasAllFields && hasNoErrors;
      },
      
      isBankDetailsValid: () => {
        const { bankDetails, errors } = get();
        const requiredFields = ['accountNumber', 'confirmAccountNumber', 'ifsc', 'bankName', 'branch', 'accountType'];
        
        // Check if all required fields are filled
        const hasAllFields = requiredFields.every(field => {
          const value = bankDetails[field];
          return value && (typeof value === 'string' ? value.trim() : value);
        });
        
        // Check if account numbers match
        const accountNumbersMatch = bankDetails.accountNumber === bankDetails.confirmAccountNumber;
        
        // Check NACH consent
        const hasNachConsent = bankDetails.nachConsent;
        
        const hasNoErrors = Object.keys(errors.bank || {}).length === 0;
        
        return hasAllFields && accountNumbersMatch && hasNachConsent && hasNoErrors;
      },
      
      isDocumentDataValid: () => {
        const { documentData, errors } = get();
        const requiredDocuments = ['profilePicture', 'aadharCard', 'panCard', 'bankStatement'];
        
        // Check if required documents are uploaded (have documentPath or uploadId)
        const hasRequiredDocuments = requiredDocuments.every(doc => {
          const docData = documentData[doc];
          const isValid = docData && (docData.documentPath || docData.uploadId);
          console.log(`Document ${doc}:`, docData, 'Valid:', isValid);
          return isValid;
        });
        
        const hasNoErrors = Object.keys(errors.documents).length === 0;
        console.log('Document validation result:', { hasRequiredDocuments, hasNoErrors, documentData });
        return hasRequiredDocuments && hasNoErrors;
      },
      
      // Reset functions
      resetPersonalDetails: () => set({
        personalDetails: {
          fullName: '',
          panNumber: '',
          gender: '',
          emailId: '',
          aadharNumber: '',
          dateOfBirth: '',
          mobile: '',
          addressLine1: '',
          addressLine2: '',
          locality: '',
          city: '',
          state: '',
          pinCode: '',
          country: 'India',
          isSameAsPermanent: true,
          currentResidenceDurationYears: '',
          currentResidenceType: ''
        },
        errors: {
          ...get().errors,
          personal: {}
        }
      }),
      
      resetProfessionalDetails: () => set({
        professionalDetails: {
          employmentType: '',
          companyName: '',
          jobFunction: '',
          designation: '',
          workSector: '',
          organization: '',
          workingSince: '',
          officialEmail: '',
          salaryReceivedType: '',
          netMonthlyIncome: '',
          workAddress: {
            address: '',
            locality: '',
            pincode: '',
            city: '',
            state: ''
          }
        },
        errors: {
          ...get().errors,
          professional: {}
        }
      }),
      
      resetBankDetails: () => set({
        bankDetails: {
          accountNumber: '',
          confirmAccountNumber: '',
          ifsc: '',
          bankName: '',
          branch: '',
          accountType: 'SAVINGS',
          upiId: '',
          nachConsent: false,
          nachConsentDate: ''
        },
        errors: {
          ...get().errors,
          bank: {}
        }
      }),
      
      resetDocumentData: () => set({
        documentData: {
          profilePicture: null,
          aadharCard: null,
          panCard: null,
          bankStatement: null,
        },
        errors: {
          ...get().errors,
          documents: {}
        }
      }),
      
      // Complete application reset
      resetApplication: () => set({
        currentStep: 1,
        leadData: null,
        selectedLoanPlan: null,
        personalDetails: {
          fullName: '',
          panNumber: '',
          gender: '',
          emailId: '',
          aadharNumber: '',
          dateOfBirth: '',
          mobile: '',
          addressLine1: '',
          addressLine2: '',
          locality: '',
          city: '',
          state: '',
          pinCode: '',
          country: 'India',
          isSameAsPermanent: true,
          currentResidenceDurationYears: '',
          currentResidenceType: ''
        },
        professionalDetails: {
          employmentType: '',
          companyName: '',
          jobFunction: '',
          designation: '',
          workSector: '',
          organization: '',
          workingSince: '',
          officialEmail: '',
          salaryReceivedType: '',
          netMonthlyIncome: '',
          workAddress: {
            address: '',
            locality: '',
            pincode: '',
            city: '',
            state: ''
          }
        },
        bankDetails: {
          accountNumber: '',
          confirmAccountNumber: '',
          ifsc: '',
          bankName: '',
          branch: '',
          accountType: 'SAVINGS',
          upiId: '',
          nachConsent: false,
          nachConsentDate: ''
        },
        documentData: {
          profilePicture: null,
          aadharCard: null,
          panCard: null,
          bankStatement: null,
        },
        errors: {
          personal: {},
          professional: {},
          bank: {},
          documents: {}
        }
      }),
      
      // Get all application data for review
      getAllApplicationData: () => {
        const state = get();
        return {
          leadData: state.leadData,
          personalDetails: state.personalDetails,
          professionalDetails: state.professionalDetails,
          bankDetails: state.bankDetails,
          documentData: state.documentData
        };
      }
    }),
    {
      name: 'application-store', // unique name for localStorage key
      partialize: (state) => ({
        // Only persist certain parts of the state
        leadData: state.leadData,
        personalDetails: state.personalDetails,
        professionalDetails: state.professionalDetails,
        bankDetails: state.bankDetails,
        documentData: state.documentData,
        currentStep: state.currentStep,
        utm: state.utm
      })
    }
  )
);

export default useApplicationStore;
